import {
  prepareRouteNoteOffer,
  ROUTE_NOTE_VERSION,
} from "@boon/x402-route";
import type { Address, Hex } from "viem";
import { API_URL } from "./api";

/**
 * Publishing a signed route note is what turns a hash-only recognition record
 * into a searchable listing. The routed Boon burn records the route ID; this
 * publishes the endpoint URL + method + a short human description that
 * recomputes to that same route ID, so the endpoint surfaces in URL search.
 *
 * The worker (`POST /api/v1/x402/route-contexts`) requires the offer signer to
 * be a recorded route participant (tipper, endpoint, or network wallet). The
 * recognizer is the tipper, so they are authorized. This seeds a minimal note:
 * the note document is the endpoint itself and the offer terms are nominal
 * (the worker's publish path verifies the signature and participant role, not a
 * live paid note endpoint).
 */

const MAX_NOTE_TEXT_BYTES = 280;

export interface PublishRouteNoteInput {
  /** Exact public endpoint URL the route was recognized for. */
  endpointUrl: string;
  /** HTTP method the route was recognized for (route ID is method-sensitive). */
  method: string;
  /** What the endpoint is for. 1-280 UTF-8 bytes. */
  intent: string;
  /** Who or what the endpoint fits. 1-280 UTF-8 bytes. */
  fit: string;
  /** Tipper wallet that signs the note (a recorded route participant). */
  publisher: Address;
  /** x402 payment asset (USDC on Base). */
  asset: Address;
  /** Base chain id. */
  chainId: number;
  /**
   * EIP-712 signer, e.g. wagmi `signTypedDataAsync`. Receives the offer typed
   * data and returns a signature over it.
   */
  signTypedData: (typedData: {
    account: Address;
    domain: Record<string, unknown>;
    types: Record<string, unknown>;
    primaryType: string;
    message: Record<string, unknown>;
  }) => Promise<Hex>;
  /** Unix seconds, injected so callers control the clock in tests. */
  now?: number;
}

export interface PublishRouteNoteResult {
  routeId: string;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function assertNoteText(value: string, field: string): void {
  const length = utf8ByteLength(value);
  if (length < 1 || length > MAX_NOTE_TEXT_BYTES) {
    throw new Error(`${field} must be 1-${MAX_NOTE_TEXT_BYTES} characters`);
  }
}

/**
 * Build, sign, and publish the route note. Returns the recomputed route ID the
 * worker recorded the description under (matches the recognized route).
 */
export async function publishRouteNote(input: PublishRouteNoteInput): Promise<PublishRouteNoteResult> {
  assertNoteText(input.intent, "intent");
  assertNoteText(input.fit, "fit");

  const createdAt = input.now ?? Math.floor(Date.now() / 1000);
  // A generous window so the description stays valid; the offer cannot outlive
  // the note, so both share the same expiry.
  const expiresAt = createdAt + 365 * 24 * 60 * 60;

  const note = {
    version: ROUTE_NOTE_VERSION,
    route: {
      publicEndpointUrl: input.endpointUrl,
      method: input.method,
    },
    intent: input.intent,
    fit: input.fit,
    limits: [],
    requestSchemaUri: null,
    documentationUri: null,
    contextUris: [],
    createdAt,
    expiresAt,
  };

  // The bytes we hash must be the exact bytes we POST as noteJson, so the
  // worker's recomputed responseDigest matches the signed offer.
  const noteJson = JSON.stringify(note);
  const noteBytes = new TextEncoder().encode(noteJson);

  const prepared = prepareRouteNoteOffer(noteBytes, {
    noteEndpoint: input.endpointUrl,
    method: input.method,
    network: `eip155:${input.chainId}` as `eip155:${number}`,
    asset: input.asset,
    payTo: input.publisher,
    // Nominal minimum; the note is a discovery record, not a live paid document.
    amountAtomic: "1",
    expiresAt,
  });

  const signature = await input.signTypedData({
    account: input.publisher,
    domain: prepared.typedData.domain as Record<string, unknown>,
    types: prepared.typedData.types as Record<string, unknown>,
    primaryType: prepared.typedData.primaryType,
    message: prepared.typedData.message as Record<string, unknown>,
  });

  const offerJson = JSON.stringify(prepared.offer, (_key, value) =>
    typeof value === "bigint" ? value.toString() : value,
  );

  const response = await fetch(`${API_URL}/api/v1/x402/route-contexts`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ noteJson, offerJson, signature }),
  });
  const body = (await response.json().catch(() => null)) as { routeId?: string; error?: string } | null;
  if (!response.ok) {
    const detail = body && typeof body.error === "string" ? `: ${body.error}` : "";
    throw new Error(`Publishing the endpoint description failed (${response.status})${detail}`);
  }
  if (!body || typeof body.routeId !== "string" || !/^0x[0-9a-fA-F]{64}$/.test(body.routeId)) {
    throw new Error("The route-context response was malformed.");
  }
  return { routeId: body.routeId };
}
